import React, { useState, useEffect, useRef } from 'react';

/**
 * Modal component that warns users about impending auto-logout
 * Self-contained countdown - manages timer internally
 */
const AutoLogoutWarningModal = ({ 
  isOpen, 
  initialTimeRemaining, 
  onStayLoggedIn, 
  onLogoutNow 
}) => {
  const [countdown, setCountdown] = useState(Math.ceil(initialTimeRemaining / 1000));
  const intervalRef = useRef(null); // Store interval ID
  const hasLoggedOutRef = useRef(false); // Prevent multiple logout calls

  // Initialize countdown when modal opens
  useEffect(() => {
    if (isOpen) {
      setCountdown(Math.ceil(initialTimeRemaining / 1000));
      hasLoggedOutRef.current = false; // Reset flag
    }
  }, [isOpen, initialTimeRemaining]);

  // Self-contained countdown timer
  useEffect(() => {
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (!isOpen) return;

    intervalRef.current = setInterval(() => {
      setCountdown(prev => {
        const newValue = prev - 1;
        
        // Auto-logout when countdown reaches 0
        if (newValue <= 0 && !hasLoggedOutRef.current) {
          hasLoggedOutRef.current = true;
          
          // Clear interval immediately
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          
          // Trigger logout
          onLogoutNow();
          return 0;
        }
        
        return Math.max(0, newValue); // Never go negative
      });
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isOpen, onLogoutNow]);

  if (!isOpen) return null;

  // Never display negative time
  const safeCountdown = Math.max(0, countdown);
  const minutes = Math.floor(safeCountdown / 60);
  const seconds = safeCountdown % 60;
  const timeDisplay = `${minutes}:${seconds.toString().padStart(2, '0')}`;

  // Visual urgency in last 30 seconds
  const isUrgent = safeCountdown <= 30;
  const timerColor = isUrgent ? 'text-red-600' : 'text-teal-600';
  const iconBgColor = isUrgent ? 'bg-red-100' : 'bg-yellow-100';
  const iconColor = isUrgent ? 'text-red-600' : 'text-yellow-600';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <div className="text-center">
          {/* Warning Icon */}
          <div className={`mx-auto flex items-center justify-center h-12 w-12 rounded-full ${iconBgColor} mb-4`}>
            <svg
              className={`h-6 w-6 ${iconColor}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>

          {/* Title */}
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {isUrgent ? '⚠️ Session Expiring Soon!' : 'Session Timeout Warning'}
          </h3>

          {/* Message */}
          <p className="text-gray-600 mb-4">
            You'll be logged out in <span className={`font-bold ${timerColor}`}>{timeDisplay}</span> due to inactivity.
          </p>

          {/* Countdown Timer */}
          <div className="mb-6">
            <div className={`text-3xl font-bold ${timerColor} mb-2 ${isUrgent ? 'animate-pulse' : ''}`}>
              {timeDisplay}
            </div>
            <p className="text-sm text-gray-500">
              Click "Stay Logged In" to continue your session
            </p>
          </div>

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={onStayLoggedIn}
              className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
            >
              Stay Logged In
            </button>
            <button
              onClick={onLogoutNow}
              className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
            >
              Logout Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutoLogoutWarningModal;